import { useId, useState } from "react";
import type { PieceRunEmployee, PieceRunSummary, PieceTicket } from "@/api/types";
import { formatMoney } from "@/lib/format";
import "./PayRunPieceSection.css";

/**
 * ef-060-8 — "Piece pay" inside the pay run's region: per worker the piece total, a per-worker ticket
 * disclosure, and (draft only) a flag on a worker finalize will ADD a line for.
 *
 * Rendered ONLY when the run has piece pay (the caller passes `summary` and this returns null on none),
 * so every pay run without it reads the DOM it always did. Lists, never a table: the pay run screen
 * keeps exactly one table.
 *
 * @ui-check: draft copy says "added when you finalize"; finalized copy says "included in this run".
 * "No hours this period" shows only while the run is a draft — once finalized the worker has a line.
 */
export interface PayRunPieceSectionProps {
  summary: PieceRunSummary | null;
  error: string | null;
  /** True once the run is finalized (or posted): the copy and the no-hours flag follow. */
  finalized: boolean;
}

export function PayRunPieceSection({ summary, error, finalized }: PayRunPieceSectionProps) {
  const headingId = useId();

  if (error) {
    return (
      <p className="piece-run__error" role="alert">
        {error}
      </p>
    );
  }
  if (!summary || summary.voided || summary.employees.length === 0) return null;

  return (
    <section
      className="piece-run"
      aria-labelledby={headingId}
      data-state={finalized ? "finalized" : "draft"}
    >
      <h3 id={headingId} className="piece-run__title">
        Piece pay
      </h3>
      <p className="piece-run__copy">
        {finalized
          ? `Piece pay included in this run: ${formatMoney(summary.total)}.`
          : `Piece pay added when you finalize: ${formatMoney(summary.total)}.`}
      </p>
      <ul className="piece-run__list" aria-label="Piece pay">
        {summary.employees.map((emp) => (
          <PieceEntry
            key={emp.employeeId}
            employee={emp}
            tickets={summary.tickets.filter((t) => t.employeeId === emp.employeeId && t.status !== "void")}
            finalized={finalized}
          />
        ))}
      </ul>
    </section>
  );
}

function PieceEntry({
  employee,
  tickets,
  finalized,
}: {
  employee: PieceRunEmployee;
  tickets: PieceTicket[];
  finalized: boolean;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const label = `${employee.employeeName} piece tickets`;
  const noHours = !finalized && !employee.hasLine;

  return (
    <li className="piece-run__entry" data-state={noHours ? "piece-only" : "ready"}>
      <div className="piece-run__row">
        <span className="piece-run__name">{employee.employeeName}</span>
        {noHours && <span className="piece-run__flag">No hours this period</span>}
        <span className="piece-run__amount">{formatMoney(employee.amount)}</span>
        <button
          type="button"
          className="btn btn--ghost piece-run__drill"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {employee.ticketCount === 1 ? "1 ticket" : `${employee.ticketCount} tickets`}
          {/* The visible words stay in the accessible name; the worker's name makes it unique. */}
          <span className="visually-hidden"> — {label}</span>
        </button>
      </div>
      {open && (
        <ul id={listId} className="piece-run__tickets" aria-label={label}>
          {tickets.map((t) => (
            <li key={t.id} className="piece-run__ticket">
              <span>
                {t.pieceTypeName} · {t.units} {t.unit}
              </span>
              <span className="piece-run__amount">{formatMoney(t.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
